<script setup lang="ts">
import BaseTag from './BaseTag.vue';
import LoginDialog from '../dialogs/LoginDialog.vue';

import { computed, ref } from 'vue';
import { formatDistanceDateToNow } from '@/utils/date-util';
import { formsService } from '@/services/forms.service';
import { useUserStore } from '@/store/userStore';
import { Locales } from '@/store/language.interface';
import { getLabel } from '@/utils/data-display-utils';
import { showSaveErrorToast } from '@/utils/save-error-toast';
import { getFullUserName } from '@/utils/user-display';

// PROPS
const props = defineProps<{
  userComment: {
    admin_moderation_reason: boolean | null;
    comment: string;
    created_at: string;
    deleted_at: string;
    form_id: number;
    id: number;
    isMandatory: boolean;
    status: number;
    summary: string;
    toxicity: number;
    updated_at: string;
    vote: any;
    user: {
      first_name: string | null;
      last_name: string | null;
    };
    user_id: number;
    likes: any[];
  };
  isProposal: boolean;
  formId: number;
  showQuestionResults: boolean;
  displayDemographicData?: boolean;
  languageEnabled?: Locales[];
}>();
const userStore = useUserStore();

// CONST
const hasLike = ref<boolean>(
  userStore.isUserLogged ? props.userComment.likes?.findIndex((l) => l.user_id === userStore.getUserId) >= 0 : false,
);
const loginDialogRef = ref(null);
const likesCount = ref<number>(props.userComment.likes?.length || 0);

// FUNCTIONS
// Not saved: the heart and the count go back to what they showed before the click.
function onLikeFailed(error: unknown) {
  console.error(error);
  hasLike.value = !hasLike.value;
  likesCount.value += hasLike.value ? 1 : -1;
  showSaveErrorToast('like', props.languageEnabled);
}

function likeComment() {
  if (!props.showQuestionResults) {
    if (userStore.isUserLogged) {
      const commentId = props.userComment.id;
      const formId = props.formId;
      if (!hasLike.value) {
        hasLike.value = true;
        likesCount.value++;
        formsService.postCommentAction(formId, commentId, 'like').catch(onLikeFailed);
      } else {
        hasLike.value = false;
        likesCount.value--;
        formsService.postCommentAction(formId, commentId, 'unlike').catch(onLikeFailed);
      }
    } else {
      loginDialogRef.value.openDialog();
    }
  }
}

// COMPUTED
const isDisagree = computed(() => {
  if (props.userComment.vote.value_id > 2) {
    return 'disagree';
  } else {
    return 'agree';
  }
});

const userDisplayName = computed(() =>
  props.displayDemographicData ? getFullUserName(props.userComment.user) : undefined,
);
</script>

<template>
  <div class="base-comment-card" :class="isProposal ? isDisagree : ''">
    <div class="base-comment-card__comments">
      <div class="base-comment-card__comment">
        <div class="base-comment-card__comment-text">
          {{ props.userComment.comment }}
        </div>
      </div>
      <div class="base-comment-card__actions">
        <div class="base-comment-card__actions-favorite" @click="likeComment">
          <div class="base-comment-card__favorite-count">
            {{ likesCount }}
            <q-icon :name="hasLike ? 'favorite' : 'favorite_border'" />
          </div>
        </div>
      </div>
    </div>
    <div class="base-comment-card__comments-metadata">
      <div class="base-comment-card__comments-opinion">
        <BaseTag v-if="isProposal">
          <template v-slot:title v-if="isDisagree === 'agree'">
            {{ getLabel('buttons.agree', props.languageEnabled) }}
          </template>
          <template v-slot:title v-else> {{ getLabel('buttons.disagree', props.languageEnabled) }}</template>
        </BaseTag>
      </div>
      <div class="base-comment-card__comments-right">
        <div class="user" v-if="userDisplayName">{{ userDisplayName }},</div>
        <div class="date">
          <i> {{ formatDistanceDateToNow(new Date(props.userComment.updated_at), props.languageEnabled) }}</i>
        </div>
      </div>
    </div>
    <LoginDialog ref="loginDialogRef" :language-enabled="props.languageEnabled" />
  </div>
</template>

<style lang="scss">
.base-comment-card {
  display: flex;
  background-color: #f0f0f0;
  border-radius: 8px;
  flex-direction: column;
  margin: 7px 0 7px 0;
  padding: 10px;

  &__comments {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &-input {
      display: flex;
      background-color: #f0f0f0;
      border-radius: 8px;
      flex-direction: row;
      margin: 7px 0 7px 0;
      padding: 5px;
    }

    &-metadata {
      display: flex;
      flex-direction: row;
      margin: $space-sm 0 0 0;
      justify-content: space-between;
    }

    &-opinion {
      display: flex;
      align-items: center;
    }

    &-right {
      display: flex;
      flex-direction: row;
      gap: $space-xs;

      .user,
      .date {
        display: flex;
        justify-content: end;
      }
    }
  }
  &__comment-text {
    white-space: pre-wrap;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    color: $color-primary-darken;
    cursor: pointer;
    min-width: 10%;
    gap: $space-xs;

    &-favorite {
      display: flex;
      justify-content: center;
    }
  }

  &__comment {
    display: flex;
    flex-direction: column;
  }

  //color bindings for each comment based on opinion
  &.disagree {
    background-color: $color-red-12;
  }

  &.agree {
    background-color: $color-green-12;
  }
}

.base-tag {
  //color bindings for each comment based on opinion
  &.strongly_disagree {
    background-color: $color-red-12;
  }

  &.disagree {
    background-color: $color-red-12;
  }

  &.strongly_agree {
    background-color: $color-green-12;
  }

  &.agree {
    background-color: $color-green-12;
  }

  &.strong {
    &.strongly_disagree {
      background-color: $color-red-50;
    }

    &.disagree {
      background-color: $color-red-50;
    }

    &.strongly_agree {
      background-color: $color-green-50;
    }

    &.agree {
      background-color: $color-green-50;
    }
  }
}
</style>
